import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Smartphone, Watch, Tablet, ImageOff, X, ZoomIn, Maximize2, Minimize2 } from "lucide-react";
import type { DeviceCategory } from "@/data/catalog";
import { getMedia, useMediaStore, type MediaItem } from "@/stores/mediaStore";
import { useStorefrontTheme } from "@/stores/storefrontThemeStore";

interface DeviceMediaGalleryProps {
  deviceName?: string;
  category?: DeviceCategory;
  conditionLabel?: string;
  mediaKey?: string;
}

const categoryIconMap: Record<string, typeof Smartphone> = {
  iphone: Smartphone, samsung: Smartphone, "apple-watch": Watch, "samsung-watch": Watch,
  "pixel-watch": Watch, ipad: Tablet, "android-tablet": Tablet,
};

const DeviceMediaGallery = ({ deviceName, category, conditionLabel, mediaKey }: DeviceMediaGalleryProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [expanded, setExpanded] = useState(false);
  const lastTouchDist = useRef<number | null>(null);
  const lastTouchCenter = useRef<{ x: number; y: number } | null>(null);
  // Selector subscriptions only — reading the whole store object caused this
  // gallery (rendered inside every storefront overlay) to rerender on any
  // theme change, contributing to sluggish overlay open/close and grade swaps.
  const mediaGallerySticky = useStorefrontTheme(s => s.mediaGallerySticky);
  const customCategoryIcon = useStorefrontTheme(s => (category ? s.categoryIcons?.[category] : undefined));
  const mediaRev = useMediaStore(s => s._rev);
  // STRICT resolution — media must match the exact selected configuration
  // (deviceName | variant | grade | screen [| subVariants]). No cross-grade /
  // cross-screen / cross-sub-variant leakage. If nothing matches the chosen
  // configuration, fall back ONLY to an explicit "Placeholder" entry, then
  // show the placeholder UI. This rule applies to all storefronts.
  const media: MediaItem[] = useMemo(() => {
    if (!mediaKey && !deviceName) return [];

    // 1. No configuration selected yet (device known but no grade/screen
    //    chosen) — preview the device using whatever media the admin has
    //    already uploaded for this device. Priority:
    //      a) explicit "Placeholder" entry,
    //      b) the first media key that belongs to this device (any
    //         variant/grade/screen) so the customer always sees the
    //         specific device they picked, never a generic icon.
    if (deviceName && !mediaKey) {
      const placeholder = getMedia(`${deviceName}|all|Placeholder`);
      if (placeholder.length > 0) return placeholder;

      // Scan media store for any key starting with this device name. We
      // surface the featured image first when present, otherwise the first
      // image, otherwise the first item.
      const all = useMediaStore.getState().data;
      const deviceKeys = Object.keys(all).filter(k => k === deviceName || k.startsWith(`${deviceName}|`));
      for (const k of deviceKeys) {
        const items = (all[k] || []).slice().sort((a, b) => a.order - b.order);
        if (items.length === 0) continue;
        const featured = items.find(m => m.featured && m.type === "image");
        if (featured) return [featured];
        const firstImage = items.find(m => m.type === "image");
        if (firstImage) return [firstImage];
        return [items[0]];
      }
      return [];
    }

    // 2. Exact configured key (includes sub-variants when present)
    if (mediaKey) {
      const exact = getMedia(mediaKey);
      if (exact.length > 0) return exact;

      // 3. Same device|variant|grade|screen WITHOUT sub-variant suffix —
      //    this is still the same selected grade/screen, only the optional
      //    sub-variant qualifier is dropped. We do NOT change grade/screen.
      const parts = mediaKey.split("|");
      if (parts.length > 4) {
        const baseNoSub = parts.slice(0, 4).join("|");
        if (baseNoSub !== mediaKey) {
          const items = getMedia(baseNoSub);
          if (items.length > 0) return items;
        }
      }

      // 4. Fall back to the device's explicit Placeholder media uploaded by
      //    the admin (grade = "Placeholder" in MediaManager). This ensures
      //    customers always see the device's main image even when a specific
      //    configuration has no dedicated media yet.
      const devName = parts[0];
      if (devName) {
        const placeholder = getMedia(`${devName}|all|Placeholder`);
        if (placeholder.length > 0) return placeholder;
      }
    }

    // 5. No placeholder uploaded either — return empty so the
    //    "No media for this configuration" badge is shown.
    return [];
  }, [mediaKey, deviceName, mediaRev]);
  const hasMedia = media.length > 0;
  const activeItem = hasMedia ? media[Math.min(activeIndex, media.length - 1)] : null;
  const Icon = category ? (categoryIconMap[category] || Smartphone) : Smartphone;

  const resetZoom = useCallback(() => { setScale(1); setTranslate({ x: 0, y: 0 }); }, []);

  const openFullscreen = useCallback(() => {
    setFullscreen(true);
    resetZoom();
  }, [resetZoom]);

  const closeFullscreen = useCallback(() => {
    setFullscreen(false);
    resetZoom();
  }, [resetZoom]);

  // Pinch to zoom handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.sqrt(dx * dx + dy * dy);
      lastTouchCenter.current = {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastTouchDist.current !== null) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const scaleDelta = dist / lastTouchDist.current;
      setScale(s => Math.min(Math.max(s * scaleDelta, 1), 5));
      lastTouchDist.current = dist;
    } else if (e.touches.length === 1 && scale > 1) {
      e.preventDefault();
      setTranslate(prev => ({
        x: prev.x + e.touches[0].clientX - (lastTouchCenter.current?.x || e.touches[0].clientX),
        y: prev.y + e.touches[0].clientY - (lastTouchCenter.current?.y || e.touches[0].clientY),
      }));
      lastTouchCenter.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, [scale]);

  const handleTouchEnd = useCallback(() => {
    lastTouchDist.current = null;
    lastTouchCenter.current = null;
  }, []);

  const handleDoubleClick = useCallback(() => {
    if (scale > 1) resetZoom();
    else setScale(2.5);
  }, [scale, resetZoom]);

  // Lock body scroll when fullscreen
  useEffect(() => {
    if (fullscreen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [fullscreen]);

  // Dynamic height based on media presence and expanded state
  const containerHeight = hasMedia && activeItem
    ? (expanded ? "max-h-[500px]" : "max-h-[260px]")
    : "min-h-[200px]";

  return (
    <>
      {/* Sticky gallery */}
       <div className={`${mediaGallerySticky ? 'sticky top-0' : ''} z-30 rounded-xl bg-card shadow-card overflow-hidden`} onContextMenu={e => e.preventDefault()}>
        <div
          className={`relative flex items-center justify-center bg-secondary/30 cursor-pointer transition-all duration-300 ${hasMedia ? containerHeight : "min-h-[200px]"}`}
          onClick={hasMedia && activeItem ? openFullscreen : undefined}
        >
          {hasMedia && activeItem ? (
            <>
              {activeItem.type === "video" ? (
                <video src={activeItem.url} controls controlsList="nodownload" className={`w-full object-contain transition-all duration-300 ${expanded ? "max-h-[500px]" : "max-h-[260px]"}`} playsInline onClick={e => e.stopPropagation()} onContextMenu={e => e.preventDefault()} />
              ) : (
                <img src={activeItem.url} alt={`${deviceName} - ${conditionLabel}`} className={`w-full object-contain p-4 transition-all duration-300 ${expanded ? "max-h-[500px]" : "max-h-[260px]"} select-none pointer-events-none`} draggable={false} onContextMenu={e => e.preventDefault()} />
              )}
              <div className="absolute top-2 right-2 flex gap-1">
                <button
                  className="p-1.5 rounded-lg bg-background/80 text-foreground hover:bg-background transition-all"
                  onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}
                  title={expanded ? "Collapse" : "Expand"}
                >
                  {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
                {activeItem.type === "image" && (
                  <button className="p-1.5 rounded-lg bg-background/80 text-foreground hover:bg-background transition-all" onClick={e => { e.stopPropagation(); openFullscreen(); }}>
                    <ZoomIn className="w-4 h-4" />
                  </button>
                )}
              </div>
            </>
          ) : deviceName ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary overflow-hidden">
                {customCategoryIcon ? (
                  <img src={customCategoryIcon} alt="" className="h-full w-full object-contain p-1.5" draggable={false} />
                ) : (
                  <Icon className="h-9 w-9 text-primary" />
                )}
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-card-foreground">{deviceName}</p>
                {conditionLabel ? (
                  <>
                    <p className="mt-0.5 text-xs text-muted-foreground">{conditionLabel}</p>
                    <span className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-[10px] font-medium text-muted-foreground border border-border">
                      <ImageOff className="w-3 h-3" />
                      No media for this configuration
                    </span>
                  </>
                ) : (
                  <p className="mt-0.5 text-xs text-muted-foreground">Select a configuration to view media</p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="relative">
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/10 to-accent/30 border border-primary/10 overflow-hidden">
                  {customCategoryIcon ? (
                    <img src={customCategoryIcon} alt="" className="h-full w-full object-contain p-2" draggable={false} />
                  ) : (
                    <Smartphone className="h-10 w-10 text-primary/60" />
                  )}
                </div>
                {!customCategoryIcon && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-[10px] text-primary">📱</span>
                  </div>
                )}
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-medium text-card-foreground">
                  {category ? "Browse this category" : "No device selected"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {category ? "Pick a model above to preview media" : "Choose a category and model above to preview"}
                </p>
              </div>
            </div>
          )}
        </div>
        {hasMedia && media.length > 1 && (
          <div className="flex gap-2 p-2 overflow-x-auto">
            {media.map((item, i) => (
              <button key={item.id} onClick={(e) => { e.stopPropagation(); setActiveIndex(i); }}
                className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${i === activeIndex ? "border-primary ring-2 ring-primary/20" : "border-border opacity-60 hover:opacity-100"}`}>
                {item.type === "video" ? <div className="w-full h-full bg-secondary flex items-center justify-center"><span className="text-xs">▶</span></div> : <img src={item.url} alt="" className="w-full h-full object-contain bg-muted" />}
              </button>
            ))}
          </div>
        )}
        {hasMedia && deviceName && (
          <div className="text-center p-2 pt-0">
            <p className="text-sm font-bold text-card-foreground">{deviceName}</p>
            {conditionLabel && <p className="text-xs text-muted-foreground">{conditionLabel}</p>}
          </div>
        )}
      </div>

      {/* Fullscreen overlay */}
      {fullscreen && activeItem && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" onClick={closeFullscreen}>
          <div className="flex items-center justify-between p-3">
            <p className="text-white/70 text-xs">{deviceName} {conditionLabel && `— ${conditionLabel}`}</p>
            <button onClick={closeFullscreen} className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div
            className="flex-1 flex items-center justify-center overflow-hidden touch-none"
            onClick={e => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onDoubleClick={handleDoubleClick}
          >
            {activeItem.type === "video" ? (
              <video src={activeItem.url} controls controlsList="nodownload" className="max-w-full max-h-full" playsInline onContextMenu={e => e.preventDefault()} />
            ) : (
              <img
                src={activeItem.url}
                alt=""
                className="max-w-full max-h-full object-contain transition-transform select-none"
                style={{ transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)` }}
                draggable={false}
                onContextMenu={e => e.preventDefault()}
              />
            )}
          </div>
          {media.length > 1 && (
            <div className="flex gap-2 p-3 justify-center overflow-x-auto" onClick={e => e.stopPropagation()}>
              {media.map((item, i) => (
                <button key={item.id} onClick={(e) => { e.stopPropagation(); setActiveIndex(i); resetZoom(); }}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === activeIndex ? "border-white" : "border-white/20 opacity-60 hover:opacity-100"}`}>
                  {item.type === "video" ? <div className="w-full h-full bg-white/10 flex items-center justify-center"><span className="text-white text-xs">▶</span></div> : <img src={item.url} alt="" className="w-full h-full object-contain bg-black/40" />}
                </button>
              ))}
            </div>
          )}
          <p className="text-white/40 text-[10px] text-center pb-3">Double-tap to zoom • Pinch to zoom • Tap outside to close</p>
        </div>
      )}
    </>
  );
};
export default DeviceMediaGallery;
